import React from 'react';

import { useHealthcheck, useGetVersion } from '@vlabs/api/artemy/client';
import { InfoCircleIcon } from '@vlabs/icons';
import { Page, PageContent, PageHeader } from '@vlabs/monokit/shared/ui/page';
import { Table, Panel, Tooltip } from '@vlabs/uikit';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import st from './InfoPage.module.sass';
import packageJson from '../../../package.json';

function StateCell({ value }: { value: string }) {
  const { t } = useTranslation();
  return (
    <React.Fragment>
      <div className={cn(st.State, st[`State_${value}`])} />
      <span>{t(`info:stateList.${value}`)}</span>
    </React.Fragment>
  );
}

function HealthcheckCell({ value, isError }: { value: number | null; isError: boolean }) {
  const { t } = useTranslation();
  if (value === null) return null;
  return (
    <div className={st.HealthCheck}>
      <span>{t(`info:healthcheck.${isError ? 'badresponse' : 'goodresponse'}`)}</span>
      {!isError && !!value && (
        <Tooltip
          className="Tooltip_ui"
          data-testid="executionTimeTooltip"
          data-tooltip-content={t('info:healthcheck.executiontime', { value })}
        >
          <InfoCircleIcon className="Tooltip_icon" />
        </Tooltip>
      )}
    </div>
  );
}

function InfoPage(): React.JSX.Element {
  const { t } = useTranslation();

  const { data: health, isLoading: healthLoading, isError: healthError } = useHealthcheck(
    {},
    { query: { refetchInterval: 60000 } },
  );
  const { data: version } = useGetVersion();

  const point2Version = version
    ? `${version.Version.major}.${version.Version.minor}.${version.Version.patch}`
    : '-';

  let point2State = 'loaded';
  if (healthError) point2State = 'failed';
  else if (healthLoading) point2State = 'loading';

  const hasHealthError = !!healthError && !healthLoading;

  const data = [
    {
      title: 'UI',
      version: packageJson.version,
      state: 'loaded',
      executionTime: null,
      healthError: false,
    },
    {
      title: 'API',
      version: point2Version,
      state: point2State,
      executionTime: health?.execution_time,
      healthError: hasHealthError,
    },
  ];

  const columns = [
    { Header: t('info:table.columns.title'), accessor: 'title' },
    { Header: t('info:table.columns.version'), accessor: 'version' },
    {
      Header: t('info:table.columns.state'),
      accessor: 'state',
      Cell: StateCell,
    },
    {
      Header: t('info:table.columns.healthcheck'),
      id: 'healthcheck',
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ row }) => (
        <HealthcheckCell
          isError={row.original.healthError}
          value={row.original.executionTime}
        />
      ),
    },
  ];

  return (
    <Page>
      <PageContent>
        <PageHeader title={t('info:title')} />
        <Panel className="PanelAutoHeight">
          <Table
            columns={columns}
            data={data}
          />
        </Panel>
      </PageContent>
    </Page>
  );
}

export { InfoPage };
